import { useState, useEffect } from 'react';
import {
    ArrowLeft,
    Plus,
    CheckCircle2,
    XCircle,
    RefreshCw,
    Search,
    Mail,
    Calendar,
    MessageSquare,
    FileText,
    Trello,
    Loader2,
    AlertCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { initiateGoogleOAuth, isOAuthConfigured, GoogleService } from '../../services/googleAuth';
import { initiateSlackOAuth, isSlackConfigured, revokeSlackAccess } from '../../services/slackService';
import { initiateNotionOAuth, isNotionConfigured, revokeNotionAccess } from '../../services/notionService';
import { useIntegrations } from '../../contexts/IntegrationsContext';

interface Connection {
    id: string;
    name: string;
    icon: string;
    description: string;
    category: 'productivity' | 'communication' | 'calendar' | 'storage';
    service?: GoogleService | 'slack' | 'notion' | 'linear' | 'todoist' | 'asana' | 'trello' | 'dropbox' | 'drive';
    isGoogleService?: boolean;
}

// Static list of available integrations (UI configuration)
const INTEGRATIONS_CONFIG: Connection[] = [
    {
        id: 'google-calendar',
        name: 'Google Calendar',
        icon: 'logos:google-calendar',
        description: 'Sync events and schedule automatically',
        category: 'calendar',
        service: 'calendar',
        isGoogleService: true,
    },
    {
        id: 'gmail',
        name: 'Gmail',
        icon: 'logos:google-gmail',
        description: 'Turn emails into tasks and get inbox summary',
        category: 'communication',
        service: 'gmail',
        isGoogleService: true,
    },
    {
        id: 'slack',
        name: 'Slack',
        icon: 'logos:slack-icon',
        description: 'Get notifications and create tasks from messages',
        category: 'communication',
        service: 'slack',
    },
    {
        id: 'notion',
        name: 'Notion',
        icon: 'logos:notion-icon',
        description: 'Sync tasks and notes with your Notion workspace',
        category: 'productivity',
        service: 'notion',
    },
    {
        id: 'linear',
        name: 'Linear',
        icon: 'logos:linear-icon',
        description: 'Track issues and manage sprints',
        category: 'productivity',
        service: 'linear',
    },
    {
        id: 'todoist',
        name: 'Todoist',
        icon: 'logos:todoist-icon',
        description: 'Import and sync your Todoist tasks',
        category: 'productivity',
        service: 'todoist',
    },
    {
        id: 'asana',
        name: 'Asana',
        icon: 'logos:asana-icon',
        description: 'Sync projects and tasks from Asana',
        category: 'productivity',
        service: 'asana',
    },
    {
        id: 'trello',
        name: 'Trello',
        icon: 'logos:trello',
        description: 'Import boards and cards from Trello',
        category: 'productivity',
        service: 'trello',
    },
    {
        id: 'dropbox',
        name: 'Dropbox',
        icon: 'logos:dropbox',
        description: 'Access and attach files from Dropbox',
        category: 'storage',
        service: 'dropbox',
    },
    {
        id: 'drive',
        name: 'Google Drive',
        icon: 'logos:google-drive',
        description: 'Access and attach files from Drive',
        category: 'storage',
        service: 'drive',
    },
];

function getCategoryIcon(category: Connection['category']) {
    switch (category) {
        case 'calendar': return Calendar;
        case 'communication': return MessageSquare;
        case 'storage': return FileText;
        default: return Trello;
    }
}

export default function AppConnections() {
    const navigate = useNavigate();
    const { integrations, googleAccount, disconnectGoogle, updateLastSync, refreshIntegrationStatus } = useIntegrations();
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState<'connected' | 'available'>('connected');
    const [syncingId, setSyncingId] = useState<string | null>(null);

    // Refresh status on mount
    useEffect(() => {
        refreshIntegrationStatus();
    }, []);

    // Merge config with integration status
    const connectionsWithStatus = INTEGRATIONS_CONFIG.map(config => {
        const integration = integrations.find(i => i.id === config.id);
        return {
            ...config,
            connected: integration?.connected || false,
            lastSync: integration?.lastSync,
            accountEmail: integration?.accountInfo?.email,
        };
    });

    const connectedApps = connectionsWithStatus.filter(c => c.connected);
    const disconnectedApps = connectionsWithStatus.filter(c => !c.connected);

    const filteredApps = (activeTab === 'connected' ? connectedApps : disconnectedApps).filter(
        c => c.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleConnect = (connection: Connection) => {
        // For Google services, use real OAuth
        if (connection.isGoogleService && connection.service) {
            if (!isOAuthConfigured()) {
                alert('Google OAuth is not configured. Please add VITE_GOOGLE_CLIENT_ID and VITE_GOOGLE_CLIENT_SECRET to your .env file.');
                return;
            }
            initiateGoogleOAuth(connection.service as GoogleService);
            return;
        }

        // For Slack, use Slack OAuth
        if (connection.service === 'slack') {
            if (!isSlackConfigured()) {
                alert('Slack OAuth is not configured. Please add VITE_SLACK_CLIENT_ID and VITE_SLACK_CLIENT_SECRET to your .env file.');
                return;
            }
            initiateSlackOAuth();
            return;
        }

        // For Notion, use Notion OAuth
        if (connection.service === 'notion') {
            if (!isNotionConfigured()) {
                alert('Notion OAuth is not configured. Please add VITE_NOTION_CLIENT_ID and VITE_NOTION_CLIENT_SECRET to your .env file.');
                return;
            }
            initiateNotionOAuth();
            return;
        }

        // For other services, show coming soon
        alert(`${connection.name} integration coming soon!`);
    };

    const handleDisconnect = async (connection: Connection) => {
        if (connection.isGoogleService) {
            await disconnectGoogle();
        } else if (connection.service === 'slack') {
            await revokeSlackAccess();
            refreshIntegrationStatus();
        } else if (connection.service === 'notion') {
            await revokeNotionAccess();
            refreshIntegrationStatus();
        }
        // For other services, would call their specific disconnect
    };

    const handleSync = async (connectionId: string) => {
        setSyncingId(connectionId);

        // Simulate sync - in real app, this would call the respective API
        await new Promise(resolve => setTimeout(resolve, 1500));

        updateLastSync(connectionId);
        setSyncingId(null);
    };

    const oauthConfigured = isOAuthConfigured();

    return (
        <div className="pt-4 pb-8 leading-[1.5]">
            {/* Header */}
            <div className="px-4 mb-6">
                <div className="flex items-center gap-3 mb-4">
                    <button
                        onClick={() => navigate('/app/profile')}
                        className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F8FA] hover:bg-[#EEF3F6]"
                    >
                        <ArrowLeft className="w-5 h-5 text-[#0D5F6B]" />
                    </button>
                    <div>
                        <h1 className="text-xl font-semibold text-[#1A2832]">Connected Apps</h1>
                        <p className="text-sm text-[#7B8C9B]">{connectedApps.length} connected</p>
                    </div>
                </div>

                {/* OAuth Warning */}
                {!oauthConfigured && (
                    <div className="mb-4 p-3 rounded-xl bg-[#FFF4E5] border border-[#E8A547]/30 flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-[#E8A547] flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-semibold text-[#1A2832]">OAuth Not Configured</p>
                            <p className="text-xs text-[#7B8C9B]">
                                Add VITE_GOOGLE_CLIENT_ID and VITE_GOOGLE_CLIENT_SECRET to .env
                            </p>
                        </div>
                    </div>
                )}

                {/* Connected Google Account */}
                {googleAccount && (
                    <div className="mb-4 p-3 rounded-xl bg-[#D5F2EA] border border-[#3DA58A]/30">
                        <div className="flex items-center gap-3">
                            {googleAccount.picture && (
                                <img
                                    src={googleAccount.picture}
                                    alt={googleAccount.name}
                                    className="w-10 h-10 rounded-full"
                                />
                            )}
                            <div className="flex-1">
                                <p className="text-sm font-semibold text-[#1A2832]">{googleAccount.name}</p>
                                <p className="text-xs text-[#7B8C9B]">{googleAccount.email}</p>
                            </div>
                            <CheckCircle2 className="w-5 h-5 text-[#3DA58A]" />
                        </div>
                    </div>
                )}

                {/* Search */}
                <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#7B8C9B]" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search integrations..."
                        className="w-full pl-12 pr-4 py-3 rounded-xl border border-[#DCE4EA] focus:outline-none focus:border-[#0D5F6B] text-[#1A2832]"
                    />
                </div>
            </div>

            {/* Tabs */}
            <div className="px-4 mb-4">
                <div className="flex gap-2 p-1 bg-[#F5F8FA] rounded-xl">
                    <button
                        onClick={() => setActiveTab('connected')}
                        className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition ${activeTab === 'connected'
                            ? 'bg-white text-[#0D5F6B] shadow-sm'
                            : 'text-[#7B8C9B]'
                            }`}
                    >
                        Connected ({connectedApps.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('available')}
                        className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition ${activeTab === 'available'
                            ? 'bg-white text-[#0D5F6B] shadow-sm'
                            : 'text-[#7B8C9B]'
                            }`}
                    >
                        Available ({disconnectedApps.length})
                    </button>
                </div>
            </div>

            {/* Integrations List */}
            <section className="px-4">
                {filteredApps.length === 0 ? (
                    <div className="bg-white rounded-2xl p-8 text-center shadow-[0_4px_16px_rgba(13,95,107,0.08)]">
                        <Mail className="w-12 h-12 text-[#DCE4EA] mx-auto mb-3" />
                        <p className="text-sm text-[#7B8C9B]">
                            {searchQuery ? 'No integrations match your search' : 'No integrations in this category'}
                        </p>
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl overflow-hidden shadow-[0_4px_16px_rgba(13,95,107,0.08)]">
                        {filteredApps.map((connection, index) => (
                            <div
                                key={connection.id}
                                className={`p-4 ${index < filteredApps.length - 1 ? 'border-b border-[#E5EBF0]' : ''}`}
                            >
                                <div className="flex items-start gap-3">
                                    <div className="w-12 h-12 rounded-xl bg-[#F5F8FA] flex items-center justify-center flex-shrink-0">
                                        <img
                                            src={`https://api.iconify.design/${connection.icon}.svg`}
                                            alt={connection.name}
                                            className="w-6 h-6"
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-1">
                                            <h3 className="text-base font-semibold text-[#1A2832]">{connection.name}</h3>
                                            {connection.connected ? (
                                                <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#D5F2EA]">
                                                    <CheckCircle2 className="w-3 h-3 text-[#3DA58A]" />
                                                    <span className="text-xs font-semibold text-[#3DA58A]">Connected</span>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F5F8FA]">
                                                    <XCircle className="w-3 h-3 text-[#7B8C9B]" />
                                                    <span className="text-xs font-semibold text-[#7B8C9B]">Not connected</span>
                                                </div>
                                            )}
                                        </div>
                                        <p className="text-sm text-[#7B8C9B] mb-2">{connection.description}</p>

                                        {connection.connected ? (
                                            <div className="flex items-center justify-between">
                                                <div className="text-xs text-[#7B8C9B]">
                                                    {connection.accountEmail && (
                                                        <span className="mr-2">{connection.accountEmail}</span>
                                                    )}
                                                    {connection.lastSync && (
                                                        <span>Last sync: {connection.lastSync}</span>
                                                    )}
                                                </div>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleSync(connection.id)}
                                                        disabled={syncingId === connection.id}
                                                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#F5F8FA] text-[#0D5F6B] text-sm font-semibold hover:bg-[#EEF3F6] disabled:opacity-50"
                                                    >
                                                        {syncingId === connection.id ? (
                                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                        ) : (
                                                            <RefreshCw className="w-3.5 h-3.5" />
                                                        )}
                                                        Sync
                                                    </button>
                                                    <button
                                                        onClick={() => handleDisconnect(connection)}
                                                        className="px-3 py-1.5 rounded-lg text-[#D66B5C] text-sm font-semibold hover:bg-[#FFEAE7]"
                                                    >
                                                        Disconnect
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => handleConnect(connection)}
                                                className="flex items-center gap-1 px-4 py-2 rounded-lg bg-[#0D5F6B] text-white text-sm font-semibold hover:bg-[#0a4d57] transition"
                                            >
                                                <Plus className="w-4 h-4" />
                                                Connect
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Categories Quick Access */}
            <section className="px-4 mt-6">
                <h2 className="text-base font-semibold text-[#1A2832] mb-3">Browse by Category</h2>
                <div className="grid grid-cols-2 gap-3">
                    {(['productivity', 'communication', 'calendar', 'storage'] as const).map(category => {
                        const Icon = getCategoryIcon(category);
                        const count = INTEGRATIONS_CONFIG.filter(c => c.category === category).length;
                        return (
                            <button
                                key={category}
                                className="p-4 bg-white rounded-xl shadow-[0_4px_16px_rgba(13,95,107,0.08)] text-left hover:bg-[#F5F8FA] transition"
                            >
                                <div className="w-10 h-10 rounded-full bg-[#D9EEF2] flex items-center justify-center mb-2">
                                    <Icon className="w-5 h-5 text-[#0D5F6B]" />
                                </div>
                                <p className="text-sm font-semibold text-[#1A2832] capitalize">{category}</p>
                                <p className="text-xs text-[#7B8C9B]">{count} apps</p>
                            </button>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}
